import { z } from 'zod';

// Single lab test result
export const labTestItemSchema = z.object({
  name: z.string().optional(),
  result: z.union([z.string(), z.number()]).optional(),
  value: z.union([z.string(), z.number()]).optional(),
  units: z.string().optional(),
  unit: z.string().optional(),
  reference_range: z
    .union([
      z.string(),
      z.object({
        min: z.union([z.string(), z.number()]).optional(),
        max: z.union([z.string(), z.number()]).optional(),
      }),
    ])
    .optional(),
  referenceRange: z.string().optional(),
  within_normal_limits: z.boolean().optional(),
  is_outside_reference_range: z.boolean().optional(),
  isOutsideReferenceRange: z.boolean().optional(),
});

export type LabTestItem = z.infer<typeof labTestItemSchema>;

// Lab results report with multiple tests
export const labResultsReportSchema = z.object({
  date: z.string().optional(),
  tests: z.array(labTestItemSchema).optional(),
});

export type LabResultsReport = z.infer<typeof labResultsReportSchema>;

// Legacy single lab result format
export const labResultDataSchema = z.object({
  id: z.string().optional(),
  name: z.string().optional(),
  value: z.union([z.string(), z.number()]).optional(),
  unit: z.string().optional(),
  reference_range: z.string().optional(),
  referenceRange: z.string().optional(),
  is_outside_reference_range: z.boolean().optional(),
  isOutsideReferenceRange: z.boolean().optional(),
  report_name: z.string().optional(),
  reportName: z.string().optional(),
  created_at: z.string().optional(),
  createdAt: z.string().optional(),
});

export type LabResultData = z.infer<typeof labResultDataSchema>;

export const medicationDataSchema = z.object({
  id: z.string().optional(),
  name: z.string().optional(),
  status: z.string().optional(),
  intent: z.string().optional(),
  dosage_instructions: z.string().optional(),
  dosageInstructions: z.string().optional(),
  authored_on: z.string().optional(),
  authoredOn: z.string().optional(),
  priority: z.string().optional(),
  note: z.string().optional(),
});

export type MedicationData = z.infer<typeof medicationDataSchema>;

export const conditionDataSchema = z.object({
  id: z.string().optional(),
  code: z.string().optional(),
  name: z.string().optional(),
  clinical_status: z.string().optional(),
  clinicalStatus: z.string().optional(),
  verification_status: z.string().optional(),
  verificationStatus: z.string().optional(),
  category: z.string().optional(),
  recorded_date: z.string().optional(),
  recordedDate: z.string().optional(),
  note: z.string().optional(),
});

export type ConditionData = z.infer<typeof conditionDataSchema>;

export const patientDetailsDataSchema = z.object({
  id: z.string().optional(),
  firstName: z.string().optional(),
  first_name: z.string().optional(),
  lastName: z.string().optional(),
  last_name: z.string().optional(),
  dateOfBirth: z.string().optional(),
  date_of_birth: z.string().optional(),
  sex: z.string().optional(),
  gender: z.string().optional(),
  address: z.string().optional(),
  email: z.string().optional(),
  cellPhoneNumber: z.string().optional(),
  cell_phone_number: z.string().optional(),
  phone: z.string().optional(),
});

export type PatientDetailsData = z.infer<typeof patientDetailsDataSchema>;

export const observationDataSchema = z.object({
  id: z.string().optional(),
  name: z.string().optional(),
  value: z.union([z.string(), z.number()]).optional(),
  unit: z.string().optional(),
  reference_range: z.string().optional(),
  referenceRange: z.string().optional(),
  is_outside_reference_range: z.boolean().optional(),
  isOutsideReferenceRange: z.boolean().optional(),
  ordinal: z.number().optional(),
  source_timestamp: z.string().optional(),
  sourceTimestamp: z.string().optional(),
});

export type ObservationData = z.infer<typeof observationDataSchema>;

export const carePlanDataSchema = z.object({
  id: z.string().optional(),
  status: z.string().optional(),
  intent: z.string().optional(),
  title: z.string().optional(),
  description: z.string().optional(),
  category: z.string().optional(),
  goals: z.union([z.array(z.string()), z.string()]).optional(),
  activity: z.union([z.array(z.string()), z.string()]).optional(),
  note: z.string().optional(),
});

export type CarePlanData = z.infer<typeof carePlanDataSchema>;

export const noteDataSchema = z.object({
  id: z.string().optional(),
  content: z.string().optional(),
  text: z.string().optional(),
  appointmentId: z.string().optional(),
  appointment_id: z.string().optional(),
  createdAt: z.string().optional(),
  created_at: z.string().optional(),
});

export type NoteData = z.infer<typeof noteDataSchema>;

export const fileDataSchema = z.object({
  text: z.string().optional(),
  fileName: z.string().optional(),
  file_name: z.string().optional(),
  startPage: z.number().optional(),
  start_page: z.number().optional(),
  endPage: z.number().optional(),
  end_page: z.number().optional(),
});

export type FileData = z.infer<typeof fileDataSchema>;

export const intakeFormDataSchema = z
  .object({
    id: z.string().optional(),
    name: z.string().optional(),
    patient_name: z.string().optional(),
    patientName: z.string().optional(),
    date: z.string().optional(),
    responses: z.record(z.unknown()).optional(),
  })
  .passthrough();

export type IntakeFormData = z.infer<typeof intakeFormDataSchema>;

// Helper functions to safely parse data
export const safeParseLabResultsReport = (data: unknown) =>
  labResultsReportSchema.safeParse(data);

export const safeParseLabResultData = (data: unknown) =>
  labResultDataSchema.safeParse(data);

export const safeParseMedicationData = (data: unknown) =>
  medicationDataSchema.safeParse(data);

export const safeParseConditionData = (data: unknown) =>
  conditionDataSchema.safeParse(data);

export const safeParsePatientDetailsData = (data: unknown) =>
  patientDetailsDataSchema.safeParse(data);

export const safeParseObservationData = (data: unknown) =>
  observationDataSchema.safeParse(data);

export const safeParseCarePlanData = (data: unknown) =>
  carePlanDataSchema.safeParse(data);

export const safeParseNoteData = (data: unknown) =>
  noteDataSchema.safeParse(data);

export const safeParseFileData = (data: unknown) =>
  fileDataSchema.safeParse(data);

export const safeParseIntakeFormData = (data: unknown) =>
  intakeFormDataSchema.safeParse(data);
